import moment from 'moment';
import type {
  TSegmentType,
  IPipelineProject,
  IHighlightsData,
  IMonthColumn,
} from './components/PipelineCalendar/type';
import { MONTH_LABELS } from './components/PipelineCalendar/const';
import { hasDelayedEventDates } from './components/PipelineCalendar/utils/delayDates';

/**
 * 给 all_project 每个 project 添加 project_id = studio_id + '_' + game_title
 */
export const getFormatProjectDatas = (allProject: any[]): any[] => {
  return (allProject || []).map((studio: any) => {
    return {
      ...studio,
      projects: (studio.projects || []).map((p: any) => {
        return {
          ...p,
          project_id: `${studio.studio_id}_${p.game_title}`,
          gameTitleAbbr: p.game_title_abbr || p.game_title,
        };
      }),
    };
  });
};

/**
 * studios转换
 */
export const getFormatStudioProjectDatas = (studios: any[]): any[] => {
  return (studios || []).map((studio: any) => {
    return {
      ...studio,
      projects: (studio.game_titles || []).map((p: any) => {
        return {
          game_title: p,
          project_id: `${studio.studio_id}_${p}`,
        };
      }),
    };
  });
};

/**
 * 将 all_project 嵌套数据按 projects 出现顺序展平，再按每个project的project_order属性排序
 * 返回 [{ game_title, project_order }, ...] 排序数组
 */
export const getSortProjectDatas = (projectDatas: any[]): any[] => {
  if (!projectDatas || !Array.isArray(projectDatas)) {
    return [];
  }

  const result: { game_title: string; project_order: number }[] = [];

  projectDatas.forEach((studio: any) => {
    if (!studio.projects || !Array.isArray(studio.projects)) {
      return;
    }

    studio.projects.forEach((p: any) => {
      if (p.game_title) {
        result.push({
          ...p,
          game_title: p.game_title,
          project_order: p.project_order ?? result.length + 1,
        });
      }
    });
  });

  // Sort by project_order, keeping original order for items without project_order
  result.sort((a, b) => {
    const orderA = a.project_order ?? Infinity;
    const orderB = b.project_order ?? Infinity;
    return orderA - orderB;
  });

  return result;
};

export const getProjectDatasTitleAbbr = (projectDatas: any[]): any[] => {
  if (!projectDatas || !Array.isArray(projectDatas)) {
    return [];
  }

  const result: { game_title: string; project_order: number }[] = [];

  projectDatas.forEach((studio: any) => {
    if (!studio.projects || !Array.isArray(studio.projects)) {
      return;
    }

    studio.projects.forEach((p: any) => {
      if (p.game_title) {
        result.push({
          ...p,
          game_title: p.game_title,
          gameTitleAbbr: p.game_title_abbr || p.game_title,
        });
      }
    });
  });

  return result;
};


/**
 * Compute highlights data from projects for the current and next month.
 */
export const computeHighlights = (
  projects: IPipelineProject[],
  selectedMonth?: string | null,
  segment?: TSegmentType,
): IHighlightsData | null => {

  const currentDate = selectedMonth ? moment(selectedMonth) : moment();
  const currentKey = currentDate.format('YYYY-MM');
  const nextKey = currentDate.clone().add(1, 'month').format('YYYY-MM');

  const grCheckInCurrent: any[] = [];
  const toBeLaunchedCurrent: any[] = [];
  const majorUpdatesCurrent: any[] = [];
  const riskOfDelay: any[] = [];
  const grCheckInNext: any[] = [];
  const toBeLaunchedNext: any[] = [];

  projects.forEach(proj => {
    const currentEvents = proj.events[currentKey] || [];
    const nextEvents = proj.events[nextKey] || [];
    currentEvents.forEach((evt: any) => {
      if (
        evt.type === 'gr_meeting'
        || evt.type === 'check_in'
        || evt.type === 'checkin_meeting'
      ) {
        grCheckInCurrent.push({
          ...proj,
          id: proj.id,
          project: proj.project,
          gameTitleAbbr: proj.gameTitleAbbr,
          cover: proj.cover,
        });
      }
      if (evt.type === 'to_be_launched') {
        toBeLaunchedCurrent.push({
          ...proj,
          id: proj.id,
          project: proj.project,
          gameTitleAbbr: proj.gameTitleAbbr,
          cover: proj.cover,
        });
      }
      if (evt.type === 'major_updates') {
        majorUpdatesCurrent.push({
          ...proj,
          id: proj.id,
          project: proj.project,
          gameTitleAbbr: proj.gameTitleAbbr,
          cover: proj.cover,
        });
      }
    });

    nextEvents.forEach((evt: any) => {
      if (
        evt.type === 'gr_meeting'
        || evt.type === 'check_in'
        || evt.type === 'checkin_meeting'
      ) {
        grCheckInNext.push({
          ...proj,
          id: proj.id,
          project: proj.project,
          gameTitleAbbr: proj.gameTitleAbbr,
          cover: proj.cover,
        });
      }
      if (evt.type === 'to_be_launched') {
        toBeLaunchedNext.push({
          ...proj,
          id: proj.id,
          project: proj.project,
          gameTitleAbbr: proj.gameTitleAbbr,
          cover: proj.cover,
        });
      }
    });

    if (
      segment === 'in_dev'
      && currentEvents.some((evt) => hasDelayedEventDates(evt))
    ) {
      riskOfDelay.push({
        ...proj,
        id: proj.id,
        project: proj.project,
        gameTitleAbbr: proj.gameTitleAbbr,
        cover: proj.cover,
      });
    }
  });

  return {
    currentMonth: {
      date: currentKey,
      grCheckInMeeting: grCheckInCurrent,
      toBeLaunched: toBeLaunchedCurrent,
      riskOfDelay,
      majorUpdates: majorUpdatesCurrent,
    },
    nextMonth: {
      date: nextKey,
      grCheckInMeeting: grCheckInNext,
      toBeLaunched: toBeLaunchedNext,
    },
    notes: {
      date: currentKey,
      content: '',
    },
  };
};


export const transformApiDataNew = (
  projectTimeline: any[],
  projectOrderList?: { game_title: string; project_order: number }[],
  projectDatasTitleAbbr?: any[],
): IPipelineProject[] => {
  if (!projectTimeline || !Array.isArray(projectTimeline)) {
    return [];
  }

  const projectMap = new Map<string, IPipelineProject>();

  projectTimeline.forEach((monthItem: any) => {
    const monthKey = monthItem.month;
    if (!monthItem.studios || !Array.isArray(monthItem.studios)) {
      return;
    }

    monthItem.studios.forEach((studio: any) => {
      if (!studio.projects || !Array.isArray(studio.projects)) {
        return;
      }

      studio.projects.forEach((proj: any) => {
        const projectId = `${studio.studio_id}-${proj.game_title}`;
        // 当allProject不为空时，给每一项添加项目名称project下的game_title_abbr，字段名叫gameTitleAbbr,若game_title_abbr为空则使用game_title
        // 先查找allProject中是与proj.game_title相同的元素
        const foundProject = projectDatasTitleAbbr?.find(item => item.game_title === proj.game_title);
        if (!projectMap.has(projectId)) {
          const statusRaw = (proj.status || '').toLowerCase();
          let normalizedStatus = '';
          if (statusRaw === 'on track') {
            normalizedStatus = 'on_track';
          } else if (statusRaw === 'delayed') {
            normalizedStatus = 'delayed';
          } else {
            normalizedStatus = statusRaw;
          }
          projectMap.set(projectId, {
            // ...proj,
            id: projectId,
            project: proj.game_title || '',
            gameTitleAbbr: foundProject?.gameTitleAbbr || proj.game_title,
            mgmt_platform: proj.mgmt_platform || '',
            cover: proj.cover || '',
            studio: studio.studio_name || '',
            platform: proj.platform || [],
            status: normalizedStatus,
            sort: projectMap.size,
            events: {},
            month: monthItem.month,
            note: monthItem.note || '',
          });
        }

        const target = projectMap.get(projectId)!;

        // Update status from latest month entry
        if (proj.status) {
          const statusRaw = (proj.status || '').toLowerCase();
          if (statusRaw === 'on track') {
            target.status = 'on_track';
          } else if (statusRaw === 'delayed') {
            target.status = 'delayed';
          } else {
            target.status = statusRaw;
          }
        }

        if (proj.events && Array.isArray(proj.events)) {
          const evtList = proj.events.map(
            (evt: any) => ({
              ...evt,
              id: studio.studio_id,
              type: evt.type || 'milestone',
              milestone: evt.milestone || '',
              start_date: evt.start_date || '',
              dates: Array.isArray(evt.dates) ? evt.dates : undefined,
              date: monthKey,
              meeting_time: evt.meeting_time || '',
              notes: evt.note || '',
              projectName: proj.game_title || '',
              gameTitleAbbr: foundProject?.gameTitleAbbr || proj.game_title,
              month: monthItem.month,
              note: monthItem.note || '',
            }),
          );

          if (!target.events[monthKey]) {
            target.events[monthKey] = [];
          }
          target.events[monthKey].push(...evtList);
        }
      });
    });
  });

  const result = Array.from(projectMap.values());

  // 按 projectOrderList 指定顺序排序（而非按 project_timeline 出现顺序）
  if (projectOrderList && Array.isArray(projectOrderList) && projectOrderList.length > 0) {
    const orderMap = new Map<string, number>();
    projectOrderList.forEach((item) => {
      orderMap.set(item.game_title, item.project_order);
    });
    result.sort((a, b) => {
      const orderA = orderMap.get(a.project) ?? Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.get(b.project) ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  // 为每个 project 的 1-12 月补齐空态 events
  const firstMonthKey = projectTimeline[0]?.month || moment().format('YYYY-MM');
  const selectedYear = moment(firstMonthKey, 'YYYY-MM').year();

  result.forEach((proj) => {
    for (let m = 1; m <= 12; m++) {
      const key = moment(`${selectedYear}-${m}`, 'YYYY-M').format('YYYY-MM');

      const keyMonthDatas = projectTimeline?.find((item: any) => {
        return item.month === key;
      });

      if (!proj.events[key] || proj.events[key].length === 0) {
        proj.events[key] = [
          {
            id: `${proj.id}-empty-${key}`,
            type: 'milestone',
            milestone: '',
            date: key,
            meeting_time: '',
            notes: '',
            projectName: proj.project,
            gameTitleAbbr: proj.gameTitleAbbr,
            mgmt_platform: proj.mgmt_platform,
            month: keyMonthDatas.month,
            note: keyMonthDatas.note,
          },
        ];
      }
    }
  });

  return result;
};


/**
 * Generate 12 month columns for the selected year only.
 * E.g. selected = 2025-03 → Jan 2025 ... Dec 2025, with Mar highlighted.
 */
export const generateMonthColumns = (
  selectedMonth?: string | null,
): IMonthColumn[] => {
  const baseDate = selectedMonth ? moment(selectedMonth) : moment();
  const columns: IMonthColumn[] = [];
  const highlightKey = baseDate.format('YYYY-MM');
  const selectedYear = baseDate.year();

  for (let i = 0; i < 12; i++) {
    const d = moment(`${selectedYear}-${i + 1}`, 'YYYY-M');
    columns.push({
      year: d.year(),
      month: d.month() + 1,
      label: MONTH_LABELS[d.month()],
      key: d.format('YYYY-MM'),
      isHighlighted: d.format('YYYY-MM') === highlightKey,
    });
  }

  return columns;
};
